import React, { useState } from 'react';
import { Box, Stack, FormControl, FormLabel, Input, Button, Chip, ChipDelete, FormHelperText } from '@mui/joy';

interface Props {
  label: string;
  helper?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export default function ChipsInput({ label, helper, values, onChange, placeholder }: Props) {
  const [input, setInput] = useState('');

  const addTokens = (raw: string) => {
    const tokens = raw
      .split(/[,\n]/)
      .map(t => t.trim())
      .filter(Boolean);
    if (!tokens.length) return;
    const next = [...values];
    for (const t of tokens) {
      if (!next.includes(t)) next.push(t);
    }
    onChange(next);
    setInput('');
  };

  const handleAdd = () => addTokens(input);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTokens(input);
    } else if (e.key === 'Backspace' && !input && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const data = e.clipboardData.getData('text');
    if (data.includes(',') || data.includes('\n')) {
      e.preventDefault();
      addTokens(data);
    }
  };

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Stack direction="row" spacing={1}>
        <Input
          value={input}
          placeholder={placeholder}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          sx={{ flex: 1 }}
        />
        <Button onClick={handleAdd} variant="soft" disabled={!input.trim()}>Add</Button>
      </Stack>
      {helper && <FormHelperText>{helper}</FormHelperText>}
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32 }}>
        {values.map(v => (
          <Chip key={v} variant="soft" color="primary" endDecorator={<ChipDelete onClick={() => onChange(values.filter(x => x !== v))} />}>
            {v}
          </Chip>
        ))}
      </Box>
    </FormControl>
  );
}
